import React, { forwardRef } from 'react'
import { Typography, TypographyProps } from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'

import { ClassesType } from '../types'

export const TextVariant = {
  chewy: 'chewy',
  hero: 'hero',
  hero2: 'hero2',
  hero3: 'hero3',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  subheading: 'subheading',
  subheading2: 'subheading2',
  subheading3: 'subheading3',
  subheading4: 'subheading4',
  subheading5: 'subheading5',
  body: 'body',
  body2: 'body2',
  body3: 'body3',
  body4: 'body4',
  body5: 'body5',
  lowAccent: 'lowAccent',
  lowAccent2: 'lowAccent2',
  lowAccent3: 'lowAccent3',
  lowAccent4: 'lowAccent4',
  lowAccent5: 'lowAccent5',
  caption: 'caption',
}

export const StrongByDefaultSet = new Set([
  TextVariant.hero,
  TextVariant.hero2,
  TextVariant.hero3,
  TextVariant.h1,
  TextVariant.h2,
  TextVariant.h3,
  TextVariant.h4,
])

const useStyles = makeStyles(
  theme => ({
    text: {},
    [TextVariant.chewy]: {
      fontSize: '1.4rem',
      color: theme.colors.chewyPrimary,
    },
    [TextVariant.hero]: {
      fontSize: '2.6rem',
      color: theme.colors.primaryText,
    },
    [TextVariant.hero2]: {
      fontSize: '2.4rem',
      color: theme.colors.primaryText,
    },
    [TextVariant.hero3]: {
      fontSize: '2.2rem',
      color: theme.colors.primaryText,
    },
    [TextVariant.h1]: {
      fontSize: '2rem',
      color: theme.colors.title,
    },
    [TextVariant.h2]: {
      fontSize: '2rem',
      color: theme.colors.primaryText,
    },
    [TextVariant.h3]: {
      fontSize: '2rem',
      color: theme.colors.secondaryText,
    },
    [TextVariant.h4]: {
      fontSize: '1.6rem',
      color: theme.colors.title,
    },
    [TextVariant.h5]: {
      fontSize: '1.4rem',
      color: theme.colors.title,
    },
    [TextVariant.subheading]: {
      fontSize: '1.8rem',
      color: theme.colors.primaryText,
    },
    [TextVariant.subheading2]: {
      fontSize: '1.6rem',
      color: theme.colors.primaryText,
    },
    [TextVariant.subheading3]: {
      fontSize: '1.4rem',
      color: theme.colors.primaryText,
    },
    [TextVariant.subheading4]: {
      fontSize: '1.2rem',
      color: theme.colors.primaryText,
    },
    [TextVariant.subheading5]: {
      fontSize: '1rem',
      color: theme.colors.primaryText,
    },
    [TextVariant.body]: {
      color: theme.colors.secondaryText,
      fontSize: '1.6rem',
    },
    [TextVariant.body2]: {
      color: theme.colors.secondaryText,
      fontSize: '1.4rem',
    },
    [TextVariant.body3]: {
      color: theme.colors.secondaryText,
      fontSize: '1.2rem',
    },
    [TextVariant.body4]: {
      color: theme.colors.secondaryText,
      fontSize: '1.1rem',
    },
    [TextVariant.body5]: {
      color: theme.colors.secondaryText,
      fontSize: '1rem',
    },
    [TextVariant.lowAccent]: {
      fontSize: '1.6rem',
      color: theme.colors.lowAccentText,
    },
    [TextVariant.lowAccent2]: {
      fontSize: '1.4rem',
      color: theme.colors.lowAccentText,
    },
    [TextVariant.lowAccent3]: {
      fontSize: '1.2rem',
      color: theme.colors.lowAccentText,
    },
    [TextVariant.lowAccent4]: {
      fontSize: '1rem',
      color: theme.colors.lowAccentText,
    },
    [TextVariant.lowAccent5]: {
      fontSize: '0.8rem',
      color: theme.colors.lowAccentText,
    },
    [TextVariant.caption]: {
      fontSize: '1.2rem',
      color: theme.colors.captionText,
    },
    strong: {
      fontWeight: 500,
    },
    underline: {
      textDecoration: 'underline',
    },
    inline: {
      display: 'inline',
    },
    disabled: {
      '&&&&': {
        color: theme.colors.lowAccentText,
      },
    },
    link: {
      textDecoration: 'underline',
      cursor: 'pointer',
    },
    linkText: {
      color: theme.colors.link,
    },
    fontSize: {
      fontSize: (props: TextStyleProps) => props.fontSize,
    },
  }),
  { name: 'Text' },
)

export interface TextProps extends Omit<TypographyProps, 'variant'> {
  readonly children?: React.ReactNode
  readonly className?: string
  readonly classes?: ClassesType<typeof useStyles>
  readonly component?: React.ElementType
  readonly disabled?: boolean
  readonly fontSize?: string | number
  readonly inline?: boolean
  readonly link?: boolean
  readonly strong?: boolean
  readonly underline?: boolean
  readonly variant?: keyof typeof TextVariant
}

interface TextStyleProps extends Pick<TextProps, 'classes' | 'fontSize'> {}

/** Common typography component for Rhapsody. Used for displaying static text. */
const Text = forwardRef<HTMLSpanElement, TextProps>(function Text(
  {
    className,
    classes: classesProp,
    children,
    strong,
    underline,
    link,
    inline,
    disabled,
    fontSize,
    variant: variantProp,
    ...rest
  },
  ref,
) {
  const classes = useStyles({ classes: classesProp, fontSize })
  const variant = variantProp || TextVariant.body
  const isStrong = strong ?? StrongByDefaultSet.has(variant)

  return (
    <Typography
      className={classNames(className, classes.text, classes[variant], {
        [classes.inline]: inline,
        [classes.strong]: isStrong,
        [classes.underline]: underline,
        [classes.disabled]: disabled,
        [classes.link]: link,
        [classes.linkText]: link && !variantProp,
        [classes.fontSize]: fontSize,
      })}
      ref={ref}
      {...rest}
    >
      {children}
      {inline && ' '}
    </Typography>
  )
})

export default Text
